import type { CSSProperties, FC, PropsWithChildren, ReactNode } from 'react'

import { cn } from '@moment/design-system-ui/lib/cn'

import { CheckIcon } from '#components/_primitives/icons/check-icon'
import { Text } from '#components/controls/text'

import linkStyles from '../_primitives/link/underline-static.module.css'

// Password managers (Bitwarden, LastPass, 1Password, etc.) inject attributes like
// `bis_skin_checked="1"` into form containers, causing React hydration warnings.
export const InputsContainer: FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className = '',
}) => (
  <div className={cn('relative', 'w-full', 'space-y-6', className)}>
    {children}
  </div>
)

// Individual input containers - also targeted by password manager extensions
export const InputContainer: FC<
  PropsWithChildren<{ 'data-private'?: boolean }>
> = ({ children, 'data-private': dataPrivate }) => (
  <div className={cn('relative', 'w-full')} data-private={dataPrivate}>
    {children}
  </div>
)

export const ForgotPasswordContainer: FC<PropsWithChildren> = ({
  children,
}) => (
  <div className={cn('absolute right-0.75 top-0.25')}>
    <div className={cn('w-fit', linkStyles['underline-link'], 'group')}>
      {children}
    </div>
  </div>
)

export const CtaContainer: FC<PropsWithChildren> = ({ children }) => (
  <div
    data-testid="cta-container"
    className={cn('mt-9', 'flex flex-col justify-start gap-3.5', 'w-full')}
  >
    {children}
  </div>
)

export const ButtonDivider: FC = () => (
  <div
    className={cn(
      'w-full h-3.75',
      'flex items-center justify-center',
      'relative',
    )}
    style={
      {
        '--mask':
          'linear-gradient(to right, transparent, black 15%, black 85%, transparent)',
        mask: 'var(--mask)',
        WebkitMask: 'var(--mask)',
      } as CSSProperties
    }
  >
    <div
      className={cn(
        'px-5',
        'absolute',
        'z-10',
        'h-3.75',
        'bg-[linear-gradient(270deg,transparent_0%,#FBFBFB_20%,#FBFBFB_80%,transparent_100%)]',
        'dark:bg-[linear-gradient(270deg,transparent_0%,var(--color-surface-primary-contrast)_20%,var(--color-surface-primary-contrast)_80%,transparent_100%)]',
      )}
    >
      <Text variant="mono" className="text-(--color-text-tertiary) pt-px">
        or
      </Text>
    </div>
    <div
      className={cn(
        'w-full h-px',
        'bg-(--color-border-secondary) dark:bg-(--color-border-primary-contrast)',
      )}
    />
  </div>
)

interface BackButtonProps {
  label?: string
  className?: string
  onClick?: () => void
  'data-testid'?: string
}

export const BackButton = ({
  label = 'Back to email',
  className = '',
  onClick,
  'data-testid': dataTestid,
}: BackButtonProps) => (
  <div
    className={cn(
      'flex items-center justify-start',
      'w-full',
      'mt-17.5',
      className,
    )}
  >
    <button
      type="button"
      onClick={onClick}
      data-testid={dataTestid}
      className={cn(
        'flex gap-2 items-center',
        'outline-offset-3',
        'rounded-full pl-1 pr-2',
        'cursor-pointer',
        '-translate-x-0.5',
        'group',
        'text-(--color-text-secondary) dark:text-(--color-text-primary-contrast)',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
        className={cn(
          'w-3.5 h-3.5',
          'group-hover:-translate-x-0.5',
          'transition-transform duration-425 ease',
        )}
      >
        <path
          stroke="currentColor"
          strokeLinecap="square"
          strokeWidth=".6"
          d="M8.687 10.875 4.312 6.5l4.375-4.375"
        />
      </svg>
      <Text
        as="span"
        variant="base"
        className={cn(
          'text-(--color-text-tertiary) dark:text-(--color-text-secondary-contrast)',
          'group-hover:text-(--color-text-secondary)/70 dark:group-hover:text-(--color-text-primary-contrast)/70',
          'transition-colors duration-250 ease',
        )}
      >
        {label}
      </Text>
    </button>
  </div>
)

// Password Requirements Components:

export const PasswordRequirementsContainer: FC<
  PropsWithChildren<{ className?: string }>
> = ({ children, className = '' }) => (
  <div className={cn('space-y-4.5', className)}>{children}</div>
)

export const RequirementsChecklistContainer: FC<
  PropsWithChildren<{ className?: string }>
> = ({ children, className = '' }) => (
  <div className={cn('space-y-1', className)}>{children}</div>
)

export const StrengthMeterContainer: FC<
  PropsWithChildren<{ className?: string }>
> = ({ children, className = '' }) => (
  <div className={cn('space-y-2', className)}>{children}</div>
)

export const StrengthMeterBarsContainer: FC<
  PropsWithChildren<{ className?: string }>
> = ({ children, className = '' }) => (
  <div className={cn('h-1.25 w-full', 'flex flex-row gap-1.5', className)}>
    {children}
  </div>
)

export const StrengthMeterBar: FC<{ className?: string }> = ({
  className = '',
}) => (
  <div
    className={cn(
      'h-1.25 flex-1',
      'rounded-full',
      'bg-(--color-border-primary) dark:bg-(--color-border-secondary-contrast)',
      'transition-all duration-250 ease',
      className,
    )}
  />
)

export const StrengthTextContainer: FC<
  PropsWithChildren<{ className?: string }>
> = ({ children, className = '' }) => (
  <div className={cn('flex justify-start items-center gap-1', className)}>
    {children}
  </div>
)

export const CompromisedPasswordWarning: FC<
  PropsWithChildren<{ className?: string }>
> = ({ children, className = '' }) => (
  <div
    className={cn(
      'pb-2.25 pt-2.5 px-3 rounded-lg',
      'bg-red-100/80 dark:bg-red-900/15',
      'border border-red-300 dark:border-red-500',
      className,
    )}
  >
    {children}
  </div>
)

const RequirementItemContainer: FC<
  PropsWithChildren<{
    met?: boolean
    className?: string
  }>
> = ({ children, met = false, className = '' }) => (
  <div
    className={cn(
      'px-0.25',
      'flex items-center gap-2.5',
      'transition-colors duration-200 ease',
      met
        ? 'text-(--color-text-primary) dark:text-(--color-text-primary-contrast)'
        : 'text-(--color-text-secondary) dark:text-(--color-text-secondary-contrast)',
      className,
    )}
  >
    {children}
  </div>
)

const RequirementCheckCircle: FC<
  PropsWithChildren<{
    met?: boolean
    className?: string
  }>
> = ({ children, met = false, className = '' }) => (
  <div
    className={cn(
      '-mt-0.25',
      'w-3 h-3',
      'rounded-full',
      'flex items-center justify-center',
      'border',
      'transition-colors duration-250 ease',
      met
        ? 'bg-(--color-border-secondary) dark:bg-(--color-border-primary-contrast) border-transparent dark:border-transparent'
        : 'bg-(--color-surface-primary) dark:bg-(--color-surface-secondary-contrast) border-(--color-border-secondary) dark:border-(--color-border-primary-contrast)/80',
      className,
    )}
  >
    {children}
  </div>
)

export const RequirementItem: FC<{
  met: boolean
  text: string | ReactNode
}> = ({ met, text }) => (
  <RequirementItemContainer met={met}>
    <RequirementCheckCircle met={met}>
      {met && (
        <CheckIcon
          className={cn(
            'h-[12px] w-[12px]',
            'text-(--color-text-primary) dark:text-(--color-text-primary-contrast)',
          )}
        />
      )}
    </RequirementCheckCircle>
    <Text variant="base">{text}</Text>
  </RequirementItemContainer>
)
